import * as React from "react";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import DashboardAlert from "./DashboardAlert/DashboardAlert";
import DashboardMessages from "./DashboardMessages/DashboardMessages";
import DashboardMiscellaneous from "./DashboardMiscellaneous/DashboardMiscellaneous";
import {useAppSelector, store} from "../../../redux/store";
import {useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {dashBoardStyle} from "./dashBoardStyle";
import {getRequest, postRequest, setHeader} from "repository/genericRepository";
import {
  FETCH_DASHBOARD_DATA,
  DASHBOARD_ANNOUNCEMENT_ENDPOINT,
} from "commonUtils/globalAPISettings";
import Skeleton from "@mui/material/Skeleton";
import {
  handleAxiosErrors,
  initialMessage,
  isValidObject,
  loggedMessage,
  message,
  useAlertTimeout,
} from "commonUtils/utils";
import {
  Alerts,
  API_TIMEOUTS_MILLISECONDS,
  CPSMessages,
  PageNames,
} from "commonUtils/enumerations";
import FormValidationAlert from "components/FormComponents/Alert/FormValidationAlert";
import {Loader} from "components/FormComponents/Loader/Loader";
import {dashboardDataInitial, type DashboardProps} from "../dashboardType";
import CPSSurvey from "./CPSSurvey";
import BrowserCompatibilityWarning from "components/Dialogs/BrowserCompatibilityWarningDialog";

const Dashboard: React.FC = () => {
  const [loader, setLoader] = React.useState<boolean>(false);
  const [dashBoardData, setDashboardData] =
    React.useState<DashboardProps>(dashboardDataInitial);
  const [announcementdialogopen, setAnnounceDialogOpen] =
    React.useState<boolean>(false);
  const [showAlert, setShowAlert] = React.useState<boolean>(false);
  const [showSurvey, setShowSurvey] = React.useState<boolean>(false);
  
  const [serverValidation, setServerValidation] =
    React.useState<message>(initialMessage);

  const {authDetails, userDetails} = useAppSelector(state => {
    return state.auth;
  });

  const navigate = useNavigate();
  let [searchParams] = useSearchParams();
      const location = useLocation();
      if (isValidObject(location?.state?.params)) {
        searchParams = new URLSearchParams(location?.state?.params);
      }
  const [surveyId, setSurveyId] = React.useState<number>(Number(searchParams.get("surveyId") ?? searchParams.get("SurveyId") ?? 0, ));

  React.useEffect(() => {
    try {
      const authState = store.getState();
     
      if (!authState?.auth?.authDetails.isAuthenticated) {
        navigate("/");
      }
      const handleContextmenu = (e: {preventDefault: () => void}) => {
        e.preventDefault();
      };
      document.addEventListener("contextmenu", handleContextmenu);
      return function cleanup() {
        document.removeEventListener("contextmenu", handleContextmenu);
      };
    } catch (error: any) {
      handleAxiosErrors(error, setShowAlert, setServerValidation);
    }
  }, []);

  React.useEffect(() => {
    try {
      if((surveyId > 0)){
        setShowSurvey(true);
      }else if (userDetails?.profileId > 0) {
        getDashboard();
      }
    } catch (error: any) {
      handleAxiosErrors(error, setShowAlert, setServerValidation);
    }
  }, [userDetails]);

  const deleteMasterMessage = async (id: number) => {
    try {
      setLoader(true);
      const authState = store.getState();
      const APIHeaders = {
        Authorization: `Bearer ${authState.auth?.authDetails?.token}`,
      };
      const announcementKey = id;
      const switchedProfileId = authState.auth?.userDetails?.switchedProfileId;
      const profileID = authState.auth?.userDetails?.profileId;
      const responseData = await postRequest({
        url: DASHBOARD_ANNOUNCEMENT_ENDPOINT,
        headers: setHeader(APIHeaders),
        timeout: API_TIMEOUTS_MILLISECONDS.DASHBOARD_ANNOUNCEMENT_ENDPOINT,
        params: {announcementKey, switchedProfileId, profileID},
      });
      if (isValidObject(responseData)) {
        setAnnounceDialogOpen(false);
        useAlertTimeout(setShowAlert);
        setServerValidation({
          ...initialMessage,
          message: CPSMessages.ANNOUNCEMENTSUCCESS,
          isSuccessMessage: true,
        });
      }
      setLoader(false);
    } catch (error: any) {
      loggedMessage(error, serverValidation?.message, PageNames.DASHBOARD);

      handleAxiosErrors(error, setShowAlert, setServerValidation);
      setLoader(false);
    }
  };

  const getDashboard = React.useMemo(
    () => async () => {
      try {
        const authState = store.getState();
        const APIHeaders = {
          Authorization: `Bearer ${authState?.auth?.authDetails?.token}`,
        };
        const id = authState?.auth?.userDetails?.profileId;
        const responseData = await getRequest({
          url: FETCH_DASHBOARD_DATA + "?profileId=" + id,
          headers: setHeader(APIHeaders),
          timeout: API_TIMEOUTS_MILLISECONDS.FETCH_DASHBOARD_DATA,
          params: {},
        });
        if (isValidObject(responseData)) {
          setDashboardData(responseData);
        }
      } catch (error: any) {
        loggedMessage(error, serverValidation?.message, PageNames.DASHBOARD);
        handleAxiosErrors(error, setShowAlert, setServerValidation);
      }
    },
    [dashBoardData],
  );

  const handleSurveyClose = ()=>{
    setShowSurvey(false);
    setSurveyId(0);
    navigate("/dashboard");
  }

  return (
    <>
    <BrowserCompatibilityWarning
        shouldDisplay={Boolean(
          authDetails?.isAuthenticated && !userDetails?.isMultipleProfiles,
        )}
        storageKey={`${userDetails?.userIdentity}:dashboard`}
        persistOnRefresh={true}
      />
      <Loader open={loader} />
      {Object.keys(dashBoardData).length !== 0 ? (
        <>
          <Container maxWidth={false} sx={dashBoardStyle.containerStyle}>
            <Grid container spacing={0}>
              {showAlert && serverValidation?.message && (
                <Grid item xs={12} lg={12}>
                  <FormValidationAlert
                    closeIcon={true}
                    showAlertBox={showAlert}
                    setShowAlertBox={setShowAlert}
                    severity={
                      (serverValidation?.isSuccessMessage ?? false)
                        ? Alerts.SUCCESS
                        : (serverValidation?.isValidationMessage ?? false)
                          ? Alerts.INFO
                          : Alerts.ERROR
                    }
                    message={serverValidation?.message}
                  />
                </Grid>
              )}
            </Grid>
          </Container>
          <Container maxWidth={false} sx={dashBoardStyle.containerStyle}>
            <Grid container spacing={3}>
              <Grid item xs={12} lg={4}>
                {Object.keys(dashBoardData).length !== 0 && (
                  <DashboardAlert alertsData={dashBoardData?.dashboardAlerts} surveyAlerts={dashBoardData?.surveyAlerts}/>
                )}
              </Grid>
              <Grid item xs={12} lg={4}>
                {Object.keys(dashBoardData).length !== 0 && (
                  <DashboardMessages
                    getDashboard={() => {
                      getDashboard();
                    }}
                    deleteMasterMessage={deleteMasterMessage}
                    messagesData={dashBoardData?.announcements}
                    isViewAllAnnouncement={dashBoardData?.isViewAllAnnouncement}
                    announcementdialogopen={announcementdialogopen}
                    setAnnounceDialogOpen={setAnnounceDialogOpen}
                    showAlert={showAlert}
                    setShowAlert={setShowAlert}
                    serverValidation={serverValidation}
                    setServerValidation={setServerValidation}
                  />
                )}
              </Grid>
              <Grid item xs={12} lg={4}>
                {Object.keys(dashBoardData).length !== 0 && (
                  <DashboardMiscellaneous miscData={dashBoardData} />
                )}
              </Grid>
            </Grid>
          </Container>
        </>
      ) : (
        <Container maxWidth={false} sx={dashBoardStyle.containerStyle}>
          <Grid container spacing={4} m={"-2rem"} sx={{marginTop: "1rem"}}>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
          </Grid>
        </Container>
      )}
     {showSurvey && (<CPSSurvey surveyId={surveyId} handlePopUpClose={handleSurveyClose}></CPSSurvey>)}

    </>
  );
};

export default Dashboard;
